import React, { useContext, useEffect, useState } from "react";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import { isDeptHead, isHead, isStaffRole } from "../utils/roles";
import {
  Alert,
  Box,
  Grid,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
  Button,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Divider,
  IconButton,
  Drawer,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import theme from "../theme";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import SearchIcon from "@mui/icons-material/Search";
import { ReactComponent as ResolvedIcon } from "../assets/icons/tick-circle.svg";
import { ReactComponent as PendingIcon } from "../assets/icons/pending-circle.svg";
import { ReactComponent as RatingIcon } from "../assets/icons/star-icon.svg";
import { ReactComponent as NextIcon } from "../assets/icons/next-arrow.svg";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import GridViewIcon from "@mui/icons-material/GridView";
import { ReactComponent as ExportExcelIcon } from "../assets/icons/export-as-excel.svg";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const StaffDirectory = () => {
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));
  const [viewMode, setViewMode] = useState("card");

  const { gradientCollection } = theme;
  const [staffList, setStaffList] = useState([]);
  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [activeTab, setActiveTab] = useState("all"); // All / Good / Under Performance
  const [searchStaffForm, setSearchStaffForm] = useState(() => {
    return localStorage.getItem("staffSearch") || "";
  });

  // Department filter
  const [departmentFilter, setDepartmentFilter] = useState([]);

  const navigate = useNavigate();
  const contextData = useContext(AutoTaskAuthContext);

  const availableRoles = contextData?.availableRoles;
  const userInfo = contextData?.userInfo;
  const userStoreID = userInfo?.store_id;

  // Departments from context
  const { allKumuduDepartments, allInternalDepartments } = contextData;
  const allDepts =
    userStoreID === 0 ? allKumuduDepartments : allInternalDepartments;
  const allStaff = [];

  allDepts?.forEach((dept) => {
    dept?.staff?.forEach((staff) => {
      allStaff.push({
        ...staff,
        department: dept?.dept_name, // or dept.id if you prefer
      });
    });
  });

  // Snackbar close
  const handleClose = () => {
    setSnackBarConfig((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    localStorage.setItem("staffSearch", searchStaffForm);
  }, [searchStaffForm]);

  // Fetch staff data from API
  useEffect(() => {
    const fetchStaff = async () => {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/performance/${userStoreID}`;
      try {
        const response = await axios.get(url);
        let { statusCode, data } = response.data;
        if (statusCode === 200 && Array.isArray(data)) {
          if (isDeptHead(userInfo.role)) {
            data = data.filter(
              (item) => item.department_id === userInfo.dept_id,
            );
          } else if (isStaffRole(userInfo.role)) {
            data = data.filter((item) => item.staff_id === userInfo.id);
          }

          setStaffList(
            data
              .filter((item) => item.staff_status === "active") // ✅ keeps only active items
              .map((item) => ({
                // ✅ transforms each item
                staff_id: item.staff_id,
                staff_name: item.staff_name,
                staff_role: item.staff_role,
                dept_name: item.department_name,
                total_tickets: item.total_assigned_today || 0,
                resolved_tickets: item.completed_today || 0,
                rating: item.rating || 0,
                staff_status: item.staff_status,
                pending_ticket: item.pending_all_time,
              })),
          );
        } else {
          setSnackBarConfig({
            open: true,
            message: "Failed to fetch staff data",
            severity: "error",
          });
        }
      } catch (error) {
        setSnackBarConfig({
          open: true,
          message: "Error fetching staff data",
          severity: "error",
        });
      }
    };

    fetchStaff();
  }, []);

  // Statistics
  const total_staff = staffList.length;
  const avg_performance =
    (staffList.reduce((acc, obj) => acc + obj.rating, 0) /
      (total_staff || 1) /
      5) *
    100;
  const total_resolved = staffList.reduce(
    (acc, obj) => acc + obj.resolved_tickets,
    0,
  );

  // Handle tab change
  const handleTabChange = (key) => setActiveTab(key);

  // Handle staff click
  const handleStaffClick = (staff) => {
    navigate("/mytickets", { state: staff });
  };

  // const handleEditStaff = (staff) => {
  //   console.log(allStaff);
  //   // let selectedStaff = allStaff.find((st) => st.staff_id === staff.staff_id);
  //   if (userInfo.role === "servicehead" || userInfo.role === "storehead") {
  //     let selectedStaff = staff;
  //     // selectedStaff = { ...selectedStaff, staff_status: staff.staff_status };
  //     navigate("/create-user", { state: selectedStaff });
  //   }
  //   return;
  // };

  // Handle department checkbox change
  const handleEditStaff = async (staff) => {
    if (isHead(userInfo.role)) {
      try {
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const response = await axios.get(`${baseURL}/api/auth/user-info`, {
          params: { user_id: staff.staff_id },
        });

        const { success, data } = response.data;

        if (success) {
          // Map API response to the shape your component expects
          const selectedStaff = {
            staff_id: data.id,
            staff_name: data.name,
            staff_mobile: data.mobile,
            staff_email: data.email,
            staff_address: data.address,
            staff_role: data.role,
            staff_status: data.status, // 'active' / 'inactive'
            dept_id: data.department?.id ?? null, // from associated department
            dept_name: data.department?.name ?? "",
            // The branch they are pinned to, so the edit form's Branch
            // dropdown opens on it instead of blank.
            staff_branch_id: data.branch_id ?? "",
          };

          setActiveTab(1);
          navigate("/create-user", { state: selectedStaff });
        } else {
          console.error(
            "Failed to fetch user info:",
            response.data.statusMessage,
          );
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    }
  };

  const handleDepartmentChange = (dept) => {
    setDepartmentFilter((prev) =>
      prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept],
    );
  };

  // Unique department names from context for filter menu
  const allDepartments = allDepts?.map((dept) => dept.dept_name) || [];

  // Filter staff based on search, tab, and department
  const filteredStaffs = staffList
    .filter((staff) =>
      staff.staff_name.toLowerCase().includes(searchStaffForm.toLowerCase()),
    )
    .filter((staff) => {
      if (activeTab === "all") return true;
      if (activeTab === "good") return staff.rating >= 3.5;
      if (activeTab === "under_performance") return staff.rating < 3.5;
      return true;
    })
    .filter((staff) =>
      departmentFilter.length > 0
        ? departmentFilter.includes(staff.dept_name)
        : true,
    );

  const STAFF_TABS = [
    { key: "all", label: "All" },
    { key: "good", label: "Good" },
    { key: "under_performance", label: "Under Performance" },
  ];

  // console.log(filteredStaffs);

  const handleExportExcel = (filteredStaffs) => {
    if (!filteredStaffs || filteredStaffs.length === 0) {
      alert("No data available to export.");
      return;
    }

    // Prepare data in a clean export-friendly format
    const exportData = filteredStaffs.map((staff, index) => ({
      "S.No": index + 1,
      "Staff Name": staff.staff_name,
      Role: staff.staff_role,
      Department: staff.dept_name,
      "Total Tickets": staff.total_tickets,
      "Resolved Tickets": staff.resolved_tickets,
      "Pending Tickets": staff.pending_ticket,
      Rating: staff.rating,
      Status: staff.staff_status,
    }));

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Staff Data");

    // Generate Excel file buffer
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // Create and save the Excel file
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });

    saveAs(data, "Staff_Performance_Report.xlsx");
  };

  return (
    <Box sx={{ width: "100%", mt: isMobile ? 0 : 2 }}>
      {/* Search & Filter Section */}
      <Box
        sx={{
          pt: 1,
          bgcolor: "#fff",
          width: isMobile ? "100vw" : "100%",
          marginX: isMobile ? -2 : 0,
          px: isMobile ? 2 : 1,
          pb: 0.1,
          boxShadow: "0 4px 8px #bdbdbd",
          borderRadius: "0 0 16px 16px",
        }}
      >
        {" "}
        <Grid container spacing={2} sx={{ mb: 1 }}>
          {/* 🔹 Search Field */}
          <Grid item xs={10}>
            <TextField
              fullWidth
              placeholder="Search by name"
              variant="outlined"
              value={searchStaffForm}
              onChange={(e) => setSearchStaffForm(e.target.value)}
              multiline
              minRows={1}
              maxRows={4}
              sx={formStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* 🔹 Action Buttons (Toggle, Export, Filter) */}
          <Grid
            item
            xs={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            {/* View Toggle — only visible on non-mobile */}
            {!isMobile && (
              <Box
                onClick={() =>
                  setViewMode((prev) => (prev === "card" ? "list" : "card"))
                }
                sx={{
                  width: 44,
                  height: 44,
                  // border: "1px solid rgba(193,193,193)",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background:
                    viewMode === "list"
                      ? "linear-gradient(90deg,rgba(0, 128, 128, 1) 0%, rgba(68, 40, 160, 1) 100%)"
                      : "transparent",
                }}
              >
                {viewMode === "list" ? (
                  <FormatListBulletedIcon sx={{ color: "#fff" }} />
                ) : (
                  <GridViewIcon sx={{ color: "#888" }} />
                )}
              </Box>
            )}

            {/* 🟩 Export to Excel Button (before Filter) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                width: 44,
                height: 44,
                borderRadius: 2,
                // border: "1px solid rgba(193,193,193)",
                "&:hover": {
                  backgroundColor: "rgba(28,56,145,0.08)",
                },
              }}
              onClick={() => handleExportExcel(filteredStaffs)}
            >
              <ExportExcelIcon
                style={{ height: 28, width: 28, fill: "#1c3891" }}
              />
            </Box>

            {/* Filter Button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: { xs: "100%", sm: "100%" },
                height: "44px",
                border: `1px solid rgba(193,193,193)`,
                borderRadius: 2,
                cursor: "pointer",
                gap: 2,
                color: { sm: "#fff" },
                background: {
                  sm: "linear-gradient(90deg,rgba(0, 128, 128, 1) 0%, rgba(68, 40, 160, 1) 100%)",
                },
              }}
              onClick={() => setFilterDrawerOpen(true)}
            >
              {!isMobile && <Typography>Filter</Typography>}
              <FilterListRoundedIcon
                sx={{
                  fontSize: 22,
                  fill: { xs: "rgba(132, 128, 128, 1)", sm: "#fff" },
                }}
              />
            </Box>
          </Grid>
        </Grid>
        {/* Tab Bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
            background:
              "linear-gradient(90deg, rgba(0,173,173,0.5) 0%, rgba(68,40,160,0.5) 100%)",
            borderRadius: 2,
            px: 1,
            py: 1,
          }}
        >
          {STAFF_TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <Typography
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 40,
                  backgroundColor: isActive ? "#fff" : "transparent",
                  borderRadius: 2,
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? "black" : "#fff",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  px: 2,
                }}
              >
                {tab.label}
              </Typography>
            );
          })}
        </Box>
        {/* Stats Boxes */}
        <Box
          sx={{
            display: "flex",
            width: "100%",
            textAlign: "center",
            border: "1px solid #ccc",
            borderRadius: 2,
            overflow: "hidden",
            py: 2,
            mb: 2,
            bgcolor: "#fff",
          }}
        >
          <Box
            sx={{
              flex: 1,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: "0%",
                height: "100%",
                width: "1px",
                bgcolor: "#ccc",
              },
            }}
          >
            <Typography
              sx={{ fontSize: 16, fontWeight: 600, color: "#008080" }}
            >
              {total_staff}
            </Typography>
            <Typography sx={{ fontSize: 12 }}>Total Staff</Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: "0%",
                height: "100%",
                width: "1px",
                bgcolor: "#ccc",
              },
            }}
          >
            <Typography
              sx={{ fontSize: 16, fontWeight: 600, color: "#FFAF09" }}
            >
              {avg_performance.toFixed(1)}%
            </Typography>
            <Typography sx={{ fontSize: 12 }}>Performance</Typography>
          </Box>

          <Box flex={1}>
            <Typography
              sx={{ fontSize: 16, fontWeight: 600, color: "#0AAB05" }}
            >
              {total_resolved}
            </Typography>
            <Typography sx={{ fontSize: 12 }}>Resolved</Typography>
          </Box>
        </Box>
      </Box>
      {/* List of Staff */}
      <Box
        sx={{
          width: "100%",
          mb: 12,
          mt: 1.5,
        }}
      >
        {viewMode === "card" ? (
          <Grid container spacing={2}>
            {filteredStaffs.map((staff, i) => (
              <Grid
                item
                xs={12}
                md={isMobile ? 12 : 4} // ✅ one column if mobile, three columns otherwise
                key={staff.staff_id}
              >
                <Box
                  sx={{
                    borderRadius: "8px",
                    boxShadow: "0px 0px 5px rgba(0,0,0,0.25)",
                    cursor: "pointer",
                    px: 2,
                    py: 1,
                    bgcolor: "#fff",
                    height: "100%", // makes cards same height in grid
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  {/* Clickable card content */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStaffClick(staff);
                    }}
                  >
                    <Box>
                      {/* Staff Details */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 44,
                            height: 44,
                            borderRadius: 4.5,
                            background:
                              gradientCollection[i % gradientCollection.length],
                            boxShadow: "0 3px 4px gray",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 24,
                              fontWeight: 500,
                              color: "#fff",
                            }}
                          >
                            {staff.staff_name.slice(0, 1).toUpperCase()}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              color:
                                theme.lightTheme.ticketCard.heading.primary,
                            }}
                          >
                            {staff.staff_name} |{" "}
                            <span style={{ color: "gray", fontSize: "12px" }}>
                              {availableRoles[staff.staff_role]}
                            </span>
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                fontSize: 12,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <ResolvedIcon /> {staff?.resolved_tickets}{" "}
                              Resolved
                            </Box>
                            <Box
                              sx={{
                                fontSize: 12,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <PendingIcon style={{ height: "18px" }} />{" "}
                              {staff.pending_ticket} Pending
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      {/* PERFORMANCE SECTION */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                          mt: 2,
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography sx={{ fontSize: 12 }}>
                              Performance
                            </Typography>
                            <Box
                              sx={{
                                minWidth: 80,
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Typography sx={{ fontSize: 12 }}>
                                {(
                                  (staff.resolved_tickets /
                                    (staff.total_tickets || 1)) *
                                  100
                                ).toFixed(0)}
                                %
                              </Typography>
                            </Box>
                          </Box>

                          <LinearProgress
                            variant="determinate"
                            value={Math.min((staff.rating / 5) * 100, 100)}
                            sx={{
                              height: 8,
                              borderRadius: 5,
                              backgroundColor: "#eee",
                              "& .MuiLinearProgress-bar": {
                                background:
                                  staff.rating >= 4
                                    ? "#4caf50"
                                    : staff.rating >= 2
                                    ? "#ff9800"
                                    : "#f44336",
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Rating + Navigation */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditStaff(staff);
                        }}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <RatingIcon />
                        <Typography sx={{ fontSize: 12 }}>
                          {staff.rating}
                        </Typography>
                      </Box>
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/performance", {
                            state: {
                              staff_id: staff.staff_id,
                              staff_role: staff.staff_role,
                              staff_name: staff.staff_name,
                            },
                          });
                        }}
                      >
                        <NextIcon />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {/* Optional: Header Row for clarity on large screens */}
            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#555",
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ width: "30%" }}>Staff</Box>
                <Box sx={{ width: "40%", textAlign: "center" }}>
                  Performance
                </Box>
                <Box sx={{ width: "15%", textAlign: "center" }}>
                  Resolved / Pending
                </Box>
                <Box sx={{ width: "10%", textAlign: "right", pr: 1 }}>
                  Rating
                </Box>
              </Box>
            )}

            {/* Staff Rows */}
            {filteredStaffs.map((staff, i) => (
              <Box
                key={staff.staff_id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  bgcolor: "#fff",
                  borderRadius: 2,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={() => handleStaffClick(staff)}
              >
                {/* 🔹 Left - Avatar + Name + Role */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "30%",
                    minWidth: "180px",
                  }}
                >
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        gradientCollection[i % gradientCollection.length],
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {staff.staff_name.charAt(0).toUpperCase()}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      {staff.staff_name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "gray" }}>
                      {availableRoles[staff.staff_role]} | {staff.dept_name}
                    </Typography>
                  </Box>
                </Box>

                {/* 🔹 Middle - Performance Bar */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    width: "40%",
                    px: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      mb: 0.5,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: "#444" }}>
                      Performance
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                      {(
                        (staff.resolved_tickets / (staff.total_tickets || 1)) *
                        100
                      ).toFixed(0)}
                      %
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((staff.rating / 5) * 100, 100)}
                    sx={{
                      width: "100%",
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "#eee",
                      "& .MuiLinearProgress-bar": {
                        background:
                          staff.rating >= 4
                            ? "#4caf50"
                            : staff.rating >= 2
                            ? "#ff9800"
                            : "#f44336",
                      },
                    }}
                  />
                </Box>

                {/* 🔹 Resolved / Pending */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "15%",
                    fontSize: 12,
                    color: "#333",
                  }}
                >
                  <Typography>
                    <span style={{ color: "#0AAB05", fontWeight: 600 }}>
                      {staff.resolved_tickets}
                    </span>{" "}
                    /{" "}
                    <span style={{ color: "#FF6B00", fontWeight: 600 }}>
                      {staff.pending_ticket}
                    </span>
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "gray" }}>
                    Resolved / Pending
                  </Typography>
                </Box>

                {/* 🔹 Rating + Edit + Next */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    width: "10%",
                    gap: 1.5,
                  }}
                >
                  {/* Edit Staff (same as card view) */}
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditStaff(staff);
                    }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      cursor: "pointer",
                      gap: 0.5,
                    }}
                  >
                    <RatingIcon />
                    <Typography sx={{ fontSize: 12 }}>
                      {staff.rating}
                    </Typography>
                  </Box>

                  {/* Navigate to performance details */}
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/performance", {
                        state: {
                          staff_id: staff.staff_id,
                          staff_role: staff.staff_role,
                          staff_name: staff.staff_name,
                        },
                      });
                    }}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    <NextIcon />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {/*  new user buttons */}
      {isHead(userInfo.role) && (
        <Box
          onClick={(e) => {
            navigate("/create-user");
          }}
          sx={{
            cursor: "pointer",
            background: theme.lightTheme.header.home,
            width: 120,
            p: 1,
            borderRadius: 50,
            border: "4px solid #d4ddff",
            boxShadow: 6,
            position: "fixed",
            bottom: "calc(86px + env(safe-area-inset-bottom, 44px))",
            right: "16px",
          }}
        >
          {" "}
          <Typography
            sx={{
              textAlign: "center",
              color: "#fff",
              fontSize: 14,
            }}
          >
            Add New +
          </Typography>{" "}
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          "&.MuiSnackbar-root": {
            bottom: "56px !important",
          },
        }}
      >
        <Alert
          onClose={handleClose}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            p: 2,
            width: "100%",

            maxHeight: "60vh",
            overflowY: "auto",
            pb: "calc(56px + env(safe-area-inset-bottom, 44px))",
            zIndex: 99999,
          },
        }}
        ModalProps={{
          keepMounted: true,
          sx: {
            zIndex: 99999, // higher than header & sidebar
          },
          BackdropProps: {
            sx: {
              zIndex: 99998, // keep backdrop just below paper
            },
          },
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="subtitle1" fontWeight={700} fontSize={16}>
            Filters
          </Typography>
          <IconButton size="small" onClick={() => setFilterDrawerOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Department Filter */}
        <Typography variant="body2" fontWeight={600} mb={0.5}>
          Department
        </Typography>
        <Grid container spacing={1} mb={1}>
          {allDepartments.map((dept) => {
            const isSelected = departmentFilter.includes(dept);
            return (
              <Grid item key={dept}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleDepartmentChange(dept)}
                  sx={{
                    borderRadius: "20px",
                    textTransform: "none",
                    fontSize: "0.85rem",
                    fontWeight: 400,
                    px: 2.5, // a bit more horizontal padding
                    py: 0.8, // a bit more vertical padding
                    m: 0.3, // small margin between buttons
                    border: isSelected
                      ? `1px solid ${theme.lightTheme.themeCol}`
                      : "1px solid black",
                    backgroundColor: isSelected
                      ? theme.lightTheme.themeCol
                      : "transparent",
                    color: isSelected ? "#fff" : "black",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? theme.lightTheme.themeCol
                        : "rgba(0,0,0,0.05)",
                    },
                  }}
                >
                  {dept}
                </Button>
              </Grid>
            );
          })}
        </Grid>

        <Divider sx={{ mb: 1 }} />

        {/* Action Buttons */}
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button
            size="small"
            sx={{
              border: `2px solid ${theme.lightTheme.themeCol}`,
              color: theme.lightTheme.themeCol,
            }}
            onClick={() => setDepartmentFilter([])}
          >
            Default
          </Button>
          <Button
            size="small"
            sx={{
              border: `2px solid ${theme.lightTheme.themeCol}`,
              backgroundColor: theme.lightTheme.themeCol,
              color: "#fff",
            }}
            onClick={() => setFilterDrawerOpen(false)}
          >
            Apply
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};

const formStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    height: "44px",
    "& fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(193,193,193)",
      borderRadius: "12px",
    },
    "&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(193,193,193)",
    },
    "&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(193,193,193)",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "rgba(193,193,193)",
  },
};

export default StaffDirectory;
